import classNames from "classnames";
import { styled } from "@linaria/react";
import React from "react";
import { color, radius, fonts } from "..";
import {
  AlertLine,
  CheckboxCircleLine,
  CloseLine,
  ErrorWarningLine,
  InformationLine,
} from "../IconV2/react";
import { shadows } from "../Theme/shadows";

const INFO_CN = "info";
const SUCCESS_CN = "success";
const ERROR_CN = "error";
const WARNING_CN = "warning";

const Wrapper = styled.div`
  display: flex;
  color: ${color.hydrogen};
  border-radius: ${radius};
  border-width: 1px;
  border-style: solid;
  align-items: center;
  box-shadow: ${shadows.shadow300};
  padding: 16px;
  pointer-events: all;
  position: relative;

  &.${INFO_CN} {
    border-color: ${color.info};
    background-color: ${color.infoBackground};
  }
  &.${SUCCESS_CN} {
    border-color: ${color.success};
    background-color: ${color.successBackground};
  }
  &.${WARNING_CN} {
    border-color: ${color.warning};
    background-color: ${color.warningBackground};
  }
  &.${ERROR_CN} {
    border-color: ${color.error};
    background-color: ${color.errorBackground};
  }
`;

const StyledIcon = styled.svg`
  flex: none;
  align-self: baseline;
  margin-right: 4px;
  width: 32px;
  height: 32px;
  &.${INFO_CN} {
    color: ${color.info};
  }
  &.${SUCCESS_CN} {
    color: ${color.success};
  }
  &.${WARNING_CN} {
    color: ${color.warning};
  }
  &.${ERROR_CN} {
    color: ${color.error};
  }
`;

const CloseIconWrapper = styled.div`
  position: absolute;
  right: 5px;
  top: 5px;
`;

const CloseIcon = styled(CloseLine)`
  height: 20px;
  width: 20px;
  cursor: pointer;
  fill: ${color.titanium400};
  &:hover {
    fill: ${color.titanium600};
  }
`;

const Title = styled.div`
  font: ${fonts.h4};
  margin-bottom: 4px;
  color: ${color.carbon900};
`;

const Text = styled.div`
  color: ${color.carbon800};
`;

const CONTENT_LINE_HEIGHT = 20;
const CONTENT_MAX_LINES = 10;

const Content = styled.div`
  font: ${fonts.body1};
  flex: 1;
  max-height: ${CONTENT_LINE_HEIGHT * CONTENT_MAX_LINES}px;
  overflow-y: auto;
  overflow-wrap: anywhere;
  margin-right: 20px;
  & a {
    text-decoration: underline;
    font-weight: 600;
  }
`;

export type ToastType = "success" | "warning" | "error" | "info";
const toastDuration = {
  // null is infinite
  info: 5000,
  success: 10000,
  warning: null,
  error: null,
  sticky: null,
};

function getIcon(type: ToastType) {
  switch (type) {
    case "warning":
      return AlertLine;
    case "error":
      return ErrorWarningLine;
    case "info":
      return InformationLine;
    case "success":
      return CheckboxCircleLine;
    default: {
      const n: never = type;
      throw n;
    }
  }
}

export interface P extends Omit<React.HTMLAttributes<HTMLDivElement>, "id" | "content"> {
  id: number;
  type: ToastType;
  title?: string;
  content: React.ReactNode;
  onClose: (id: number) => void;
}

const Toast = ({ id, type, title, content, onClose, className, ...props }: P) => {
  const timeoutRef = React.useRef<number>();
  const [sticky, setSticky] = React.useState(false);

  const toggleSticky = React.useCallback(() => setSticky(sticky => !sticky), []);

  const stopTimeout = React.useCallback(() => {
    clearTimeout(timeoutRef.current);
  }, []);

  const startTimeout = React.useCallback(() => {
    stopTimeout();
    const duration = toastDuration[sticky ? "sticky" : type];
    if (duration === null) return;
    timeoutRef.current = window.setTimeout(() => {
      onClose(id);
    }, duration);
  }, [id, onClose, sticky, stopTimeout, type]);

  const handleClose = React.useCallback(() => onClose(id), [id, onClose]);

  React.useEffect(() => {
    startTimeout();
    return () => {
      stopTimeout();
    };
  }, [startTimeout, stopTimeout]);

  return (
    <Wrapper
      data-cy={`${type}-toast`}
      onDoubleClick={toggleSticky}
      onMouseEnter={stopTimeout}
      onMouseLeave={startTimeout}
      className={classNames(className, {
        [ERROR_CN]: type === "error",
        [WARNING_CN]: type === "warning",
        [INFO_CN]: type === "info",
        [SUCCESS_CN]: type === "success",
      })}
      {...props}
    >
      <StyledIcon
        as={getIcon(type)}
        className={classNames(className, {
          [ERROR_CN]: type === "error",
          [WARNING_CN]: type === "warning",
          [INFO_CN]: type === "info",
          [SUCCESS_CN]: type === "success",
        })}
      />
      <Text>
        {title && <Title>{title}</Title>}
        <Content>{content}</Content>
      </Text>

      <CloseIconWrapper>
        <CloseIcon onClick={handleClose} data-cy="close-toast-icon" />
      </CloseIconWrapper>
    </Wrapper>
  );
};

export default React.memo(Toast);
